// SupplierInventory.jsx
import React, { Component } from "react";
import CartContext from "./CartContext";
import { BiRefresh } from "react-icons/bi";
import { BsFillPencilFill } from "react-icons/bs";
import { MessageBox } from "./MessageBox";
import Fuse from "fuse.js";
import Swal from "sweetalert2";
import {
  Button,
  ButtonToolbar,
  Form,
  Row,
  Col,
  Modal,
  Nav,
  Table,
  Navbar,
  Stack,
  Spinner,
  FormControl,
} from "react-bootstrap";

export class SupplierInventory extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      filteredItems: [],
      suppliers: [],
      addModalShow: false,
      msgboxOpen: false,
      msgboxTitle: "",
      msgboxMessage: "",
      loading: false,
      searchQuery: "",
      selectedSupplierId: "",
      uploading: false,
    };
    this.inputFile = React.createRef();
    this._searchDebounce = null;
  }

  // helper to determine if current user is supplier and their supplier id
  getSupplierUserContext() {
    const ctx = this.context || {};
    const user = ctx.userData || {};
    const roles = ctx.UserRoles || {};
    const isSupplierUser =
      user && roles && user.roleId === roles.SUPPLIER ? true : false;
    const supplierUserId = user ? String(user.storeRoleValue || "") : "";
    return { isSupplierUser, supplierUserId };
  }

  componentDidMount() {
    this.loadSuppliers().then(() => this.GetInventory());
  }

  // returns promise
  loadSuppliers = () => {
    const storeId = this.context?.storeId;
    if (!storeId) return Promise.resolve([]);
    return fetch(`${process.env.REACT_APP_API}Supplier/GetSuppliers/${storeId}`)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        this.setState({ suppliers: list });
        return list;
      })
      .catch((err) => {
        console.error("loadSuppliers error", err);
        this.setState({ suppliers: [] });
        return [];
      });
  };

  GetInventory = () => {
    this.setState({ loading: true });
    fetch(
      `${process.env.REACT_APP_API}SupplierInventory/GetInventory/${this.context.storeId}`
    )
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) =>
          (a.productName || "").localeCompare(b.productName || "")
        );
        this.setState({ items: list, loading: false }, () => {
          this.applyFilters();
        });
      })
      .catch((err) => {
        console.error("GetInventory error", err);
        this.ShowMessagBox("Error", "Failed to load inventory");
        this.setState({ loading: false });
      });
  };

  onSearchChange = (q) => {
    clearTimeout(this._searchDebounce);
    this.setState({ searchQuery: q });
    this._searchDebounce = setTimeout(() => this.applyFilters(), 180);
  };

  handleSupplierFilterChange = (e) => {
    const val = e.target.value || "";
    this.setState({ selectedSupplierId: val }, () => this.applyFilters());
  };

  applyFilters = () => {
    const { items, searchQuery, selectedSupplierId } = this.state;
    let results = Array.isArray(items) ? items.slice() : [];

    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();

    // supplier users see only their rows
    if (isSupplierUser && supplierUserId) {
      results = results.filter(
        (it) =>
          (it.supplierId || "") &&
          String(it.supplierId).toLowerCase() ===
            String(supplierUserId).toLowerCase()
      );
    }

    // if non-supplier or select filter provided, apply select
    if (
      selectedSupplierId &&
      selectedSupplierId.trim() !== "" &&
      !(isSupplierUser && supplierUserId)
    ) {
      results = results.filter(
        (it) =>
          (it.supplierId || "") &&
          String(it.supplierId).toLowerCase() ===
            String(selectedSupplierId).toLowerCase()
      );
    }

    if (searchQuery && searchQuery.trim() !== "") {
      const fuse = new Fuse(results, {
        keys: ["productName", "batchId", "qualityId", "hsncode"],
        threshold: 0.25,
        ignoreLocation: true,
      });
      results = fuse.search(searchQuery).map((r) => r.item);
    }

    results = results.sort((a, b) =>
      (a.productName || "").localeCompare(b.productName || "")
    );
    this.setState({ filteredItems: results });
  };

  getSupplierLabel = (supplierId) => {
    if (!supplierId) return "";
    const idStr = String(supplierId).toLowerCase();
    const s = (this.state.suppliers || []).find(
      (x) => (x.id || "").toString().toLowerCase() === idStr
    );
    if (!s) return supplierId;
    return s.name || s.companyName || s.emailId || s.id;
  };

  ShowMessagBox = (title, message) =>
    this.setState({
      msgboxOpen: true,
      msgboxTitle: title,
      msgboxMessage: message,
    });

  onUploadButtonClick = () => {
    if (this.inputFile?.current) this.inputFile.current.click();
  };

  handleFileUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    this.setState({ uploading: true });
    try {
      // your upload flow...
      this.setState({ uploading: false });
      this.GetInventory();
    } catch (err) {
      console.error("upload error", err);
      this.setState({ uploading: false });
      this.ShowMessagBox("Error", "Upload failed");
    } finally {
      if (this.inputFile?.current) this.inputFile.current.value = null;
    }
  };

  DownloadInventoryToCSV = () => {
    const list = this.state.items || [];
    if (!list.length) {
      this.ShowMessagBox("Info", "No inventory to download");
      return;
    }
    const headers = [
      "id",
      "productName",
      "batchId",
      "qualityId",
      "hsncode",
      "inStockQty",
      "unit",
      "mrp",
      "discount",
      "gst",
      "supplierId",
      "remarks",
    ];
    const escapeCsv = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = list.map((r) =>
      headers
        .map((h) => escapeCsv(r[h] !== undefined && r[h] !== null ? r[h] : ""))
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      "download",
      `inventory_${this.context.storeId || "store"}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  render() {
    const items = (this.state.filteredItems || []).slice();
    const { suppliers, searchQuery, selectedSupplierId, loading, uploading } =
      this.state;

    const { isSupplierUser, supplierUserId } = this.getSupplierUserContext();

    return (
      <>
        {this.state.msgboxOpen && (
          <MessageBox
            open={this.state.msgboxOpen}
            title={this.state.msgboxTitle}
            message={this.state.msgboxMessage}
            callback={() => this.setState({ msgboxOpen: false })}
          />
        )}

        <input
          style={{ display: "none" }}
          type="file"
          accept=".csv"
          ref={this.inputFile}
          onChange={this.handleFileUpload}
        />

        <Navbar className="shadow position-sticky fixed-top py-3 bg-white">
          <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
            <h5 style={{ margin: 0 }}>
              <b>Supplier Inventory</b>
            </h5>

            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <ButtonToolbar>
                <Button
                  className="mainthemecolor"
                  onClick={() => this.setState({ addModalShow: true })}
                >
                  + Add
                </Button>{" "}
                <Button
                  className="mainthemecolor mx-1"
                  onClick={() => {
                    this.loadSuppliers();
                    this.GetInventory();
                  }}
                >
                  <BiRefresh size={20} />
                </Button>{" "}
                {!isSupplierUser && (
                  <>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={this.DownloadInventoryToCSV}
                    >
                      Download CSV
                    </Button>{" "}
                    &nbsp;
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={this.onUploadButtonClick}
                    >
                      {uploading ? (
                        <>
                          <Spinner animation="border" size="sm" /> Uploading...
                        </>
                      ) : (
                        "Upload CSV"
                      )}
                    </Button>
                  </>
                )}
                {isSupplierUser && (
                  <div style={{ padding: "6px 10px", color: "#666" }}>
                    Managing own products
                  </div>
                )}
              </ButtonToolbar>
            </div>
          </div>

          <Navbar.Collapse className="justify-content-end mx-0" />
        </Navbar>

        <div className="p-3" style={{ marginTop: 10 }}>
          <Stack
            direction="horizontal"
            gap={3}
            className="mb-3"
            style={{ flexWrap: "wrap" }}
          >
            <FormControl
              style={{ minWidth: 180, maxWidth: 420 }}
              type="search"
              placeholder="Search by product, batch, quality, hsn..."
              value={searchQuery}
              onChange={(e) => this.onSearchChange(e.target.value)}
            />

            <Form.Select
              style={{ minWidth: 160, maxWidth: 320 }}
              value={selectedSupplierId}
              onChange={this.handleSupplierFilterChange}
              disabled={isSupplierUser}
            >
              <option value="">— All Suppliers —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.companyName || s.emailId || s.id}
                </option>
              ))}
            </Form.Select>

            <div className="ms-auto">
              <Button
                size="sm"
                onClick={() => {
                  this.setState(
                    {
                      searchQuery: "",
                      selectedSupplierId: isSupplierUser ? supplierUserId : "",
                    },
                    () => this.applyFilters()
                  );
                }}
              >
                Clear Filters
              </Button>
            </div>
          </Stack>

          {/* Desktop/table view (md and up) */}
          <div className="d-none d-md-block">
            <Table striped bordered hover className="mt-3 bg-white small">
              <thead>
                <tr>
                  <th>#</th>
                  <th width="20%">Product</th>
                  <th width="8%">BatchNo</th>
                  <th width="8%">QualityID</th>
                  <th width="8%">HSNCode</th>
                  <th width="5%">InStk Qty</th>
                  <th width="5%">Unit</th>
                  <th width="8%">MRP</th>
                  <th width="5%">Discount</th>
                  <th width="10%">Selling Price</th>
                  <th width="5%">GST</th>
                  <th width="10%">Selling Price (with GST)</th>
                  <th>Supplier</th>
                  <th width="5%" className="text-end">
                    Options
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const rowSupplierId = it?.supplierId
                    ? String(it.supplierId)
                    : "";
                  const canEdit =
                    !isSupplierUser ||
                    (rowSupplierId &&
                      rowSupplierId.toLowerCase() ===
                        String(supplierUserId || "").toLowerCase());
                  return (
                    <EditInventoryInline
                      key={it.id || idx}
                      index={idx}
                      item={it}
                      suppliers={this.state.suppliers}
                      getSupplierLabel={this.getSupplierLabel}
                      OnDeleted={this.GetInventory}
                      canEdit={canEdit}
                      isSupplierUser={isSupplierUser}
                      supplierUserId={supplierUserId}
                    />
                  );
                })}
              </tbody>
            </Table>

            {loading && (
              <div className="text-center mt-3">
                <Spinner animation="border" />
              </div>
            )}
            {!loading && !items.length && (
              <div className="text-center text-muted mt-3">
                No inventory rows.
              </div>
            )}
          </div>

          {/* Mobile list view (xs & sm) */}
          <div className="d-block d-md-none">
            {loading && (
              <div className="text-center mt-3">
                <Spinner animation="border" />
              </div>
            )}

            {!loading && !items.length && (
              <div className="text-center text-muted mt-3">
                No inventory rows.
              </div>
            )}

            <div className="d-flex flex-column gap-3">
              {items.map((it, idx) => {
                const rowSupplierId = it?.supplierId
                  ? String(it.supplierId)
                  : "";
                const canEdit =
                  !isSupplierUser ||
                  (rowSupplierId &&
                    rowSupplierId.toLowerCase() ===
                      String(supplierUserId || "").toLowerCase());
                return (
                  <EditInventoryInline
                    key={`mobile-${it.id || idx}`}
                    index={idx}
                    item={it}
                    suppliers={this.state.suppliers}
                    getSupplierLabel={this.getSupplierLabel}
                    OnDeleted={this.GetInventory}
                    mobile
                    canEdit={canEdit}
                    isSupplierUser={isSupplierUser}
                    supplierUserId={supplierUserId}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {this.state.addModalShow && (
          <InventoryAddOrEditModal
            show={this.state.addModalShow}
            Type="Add"
            onHide={() =>
              this.setState({ addModalShow: false }, () => this.GetInventory())
            }
          />
        )}
      </>
    );
  }
}

/* =========================
   EditInventoryInline
   - respects props.canEdit and props.isSupplierUser
   - if supplier user: supplier cell becomes read-only in edit mode and supplierId is enforced on save
   ========================= */
export class EditInventoryInline extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      editMode: false,
      editItem: this.cloneItem(props.item, props),
      saving: false,
    };
  }

  cloneItem(item, props = this.props) {
    if (!item) return {};
    const base = {
      id: item.id,
      storeId: item.storeId,
      productName: item.productName ?? "",
      batchId: item.batchId ?? "",
      qualityId: item.qualityId ?? "",
      hsncode: item.hsncode ?? "",
      inStockQty: item.inStockQty ?? 0,
      unit: item.unit ?? "piece",
      mrp: item.mrp ?? 0,
      discount: item.discount ?? 0,
      gst: item.gst ?? 0,
      remarks: item.remarks ?? "",
      supplierId: item.supplierId ?? null,
    };

    // If supplier user and editing their row, prefer forcing supplierId to supplierUserId
    if (props && props.isSupplierUser && props.supplierUserId) {
      const rowId = item?.supplierId ? String(item.supplierId) : "";
      if (
        rowId &&
        rowId.toLowerCase() === String(props.supplierUserId).toLowerCase()
      ) {
        base.supplierId = props.supplierUserId;
      }
    }
    return base;
  }

  componentDidUpdate(prevProps) {
    if (!this.state.editMode && prevProps.item !== this.props.item) {
      this.setState({ editItem: this.cloneItem(this.props.item, this.props) });
    }
    // if props.isSupplierUser flipped or supplierUserId changed, ensure editItem uses correct supplierId
    if (
      !this.state.editMode &&
      (prevProps.isSupplierUser !== this.props.isSupplierUser ||
        prevProps.supplierUserId !== this.props.supplierUserId)
    ) {
      this.setState({ editItem: this.cloneItem(this.props.item, this.props) });
    }
  }

  onEdit = () => {
    const canEdit = !!this.props.canEdit;
    if (!canEdit) return;
    // if supplier user, force supplierId
    const editItem = this.cloneItem(this.props.item, this.props);
    if (this.props.isSupplierUser && this.props.supplierUserId) {
      editItem.supplierId = this.props.supplierUserId;
    }
    this.setState({ editMode: true, editItem });
  };

  onCancel = () => {
    this.setState({
      editMode: false,
      editItem: this.cloneItem(this.props.item, this.props),
    });
  };

  onChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      editItem: { ...prev.editItem, [name]: value },
    }));
  };

  onNumericChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      editItem: { ...prev.editItem, [name]: value },
    }));
  };

  getSupplierLabelLocal = (supplierId) => {
    if (!supplierId) return "";
    if (typeof this.props.getSupplierLabel === "function") {
      try {
        const l = this.props.getSupplierLabel(supplierId);
        if (l) return l;
      } catch (e) {
        console.warn("parent getSupplierLabel failed", e);
      }
    }
    const list = this.props.suppliers || [];
    if (!list.length) return supplierId;
    const normalize = (s) =>
      String(s || "")
        .toLowerCase()
        .replace(/-/g, "")
        .trim();
    const sid = normalize(supplierId);
    const found = list.find((x) => normalize(x.id) === sid);
    if (found)
      return (
        found.name || found.companyName || found.emailId || String(found.id)
      );
    const byName = list.find(
      (x) => (x.name || "").toLowerCase() === String(supplierId).toLowerCase()
    );
    if (byName) return byName.name || byName.companyName || String(byName.id);
    return supplierId;
  };

  computeSellPrice = (mrpRaw, discountRaw) => {
    const mrp = parseFloat(mrpRaw) || 0;
    const disc = parseFloat(discountRaw) || 0;
    const d = disc > 1 ? disc / 100 : disc;
    const sell = mrp - mrp * d;
    return Math.round((sell + Number.EPSILON) * 100) / 100;
  };

  computeSellPriceWithGst = (sellPrice, gstRaw) => {
    const gst = parseFloat(gstRaw) || 0;
    const gstAmount = (sellPrice * gst) / 100;
    const total = sellPrice + gstAmount;
    return Math.round((total + Number.EPSILON) * 100) / 100;
  };

  onSave = async () => {
    const canEdit = !!this.props.canEdit;
    if (!canEdit) return;

    const raw = { ...this.state.editItem };

    if (!raw.productName || !String(raw.productName).trim()) {
      Swal.fire("Validation", "Product name is required", "warning");
      return;
    }

    // Enforce supplier id on server payload if user is supplier user
    let supplierIdPayload = raw.supplierId;
    if (this.props.isSupplierUser && this.props.supplierUserId) {
      supplierIdPayload = this.props.supplierUserId;
    }

    const payload = {
      id: raw.id || undefined,
      storeId: raw.storeId || this.context.storeId,
      productName: String(raw.productName).trim(),
      batchId: raw.batchId ? String(raw.batchId).trim() : null,
      qualityId: raw.qualityId ? String(raw.qualityId).trim() : null,
      hsncode: raw.hsncode ? String(raw.hsncode).trim() : null,
      inStockQty:
        raw.inStockQty === "" || raw.inStockQty === null
          ? 0
          : parseInt(raw.inStockQty, 10) || 0,
      unit: raw.unit || "piece",
      mrp: raw.mrp === "" || raw.mrp === null ? 0 : parseFloat(raw.mrp) || 0,
      discount:
        raw.discount === "" || raw.discount === null
          ? 0
          : parseFloat(raw.discount) || 0,
      gst: raw.gst === "" || raw.gst === null ? 0 : parseInt(raw.gst, 10) || 0,
      remarks: raw.remarks || "",
      supplierId:
        supplierIdPayload && supplierIdPayload !== ""
          ? supplierIdPayload
          : null,
    };

    this.setState({ saving: true });

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}SupplierInventory/AddOrUpdateInventory`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        Swal.fire("Error", `Save failed: ${res.status} ${text}`, "error");
        this.setState({ saving: false });
        return;
      }

      this.setState({ editMode: false, saving: false });
      if (typeof this.props.OnDeleted === "function") this.props.OnDeleted();
    } catch (err) {
      console.error("Save error", err);
      Swal.fire("Error", "Save failed - see console", "error");
      this.setState({ saving: false });
    }
  };

  OnDelete = () => {
    const canEdit = !!this.props.canEdit;
    if (!canEdit) return;

    const id = this.props.item?.id;
    if (!id) return;
    Swal.fire({
      title: "Delete?",
      text: "Delete this inventory row?",
      showCancelButton: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      fetch(
        `${process.env.REACT_APP_API}SupplierInventory/DeleteInventory/${id}`,
        { method: "DELETE" }
      )
        .then((res) => {
          if (res.ok) {
            if (typeof this.props.OnDeleted === "function")
              this.props.OnDeleted();
          } else {
            Swal.fire("Warning", "Could not delete inventory", "warning");
          }
        })
        .catch((err) => {
          console.error("Delete error", err);
          Swal.fire("Error", "Delete failed", "error");
        });
    });
  };

  mobileTwoColumnTable() {
    const it = this.props.item || {};
    const { editMode, editItem, saving } = this.state;
    const supplierLabel = this.getSupplierLabelLocal(it.supplierId);
    const canEdit = !!this.props.canEdit;
    const readOnly = !canEdit;

    // compute display values for non-edit mode
    const displaySell = this.computeSellPrice(it.mrp, it.discount);
    const displaySellGst = this.computeSellPriceWithGst(displaySell, it.gst);

    return (
      <div className="border rounded p-2 bg-white">
        <Table size="sm" bordered>
          <tbody>
            <tr>
              <td style={{ width: "45%", fontWeight: 700 }}>Product</td>
              <td>
                {editMode ? (
                  <Form.Control
                    name="productName"
                    value={editItem.productName}
                    onChange={this.onChange}
                    size="sm"
                    disabled={readOnly}
                  />
                ) : (
                  it.productName
                )}
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Supplier: {supplierLabel || "—"}
                </div>
              </td>
            </tr>

            {/* other rows omitted for brevity, same as desktop but using disabled={readOnly} for inputs */}

            <tr>
              <td></td>
              <td className="text-end">
                {editMode ? (
                  <>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={this.onSave}
                      disabled={saving || readOnly}
                    >
                      {saving ? "Saving..." : "Save"}
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={this.onCancel}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    {canEdit ? (
                      <>
                        <Button
                          size="sm"
                          variant="warning"
                          onClick={this.onEdit}
                        >
                          <BsFillPencilFill />
                        </Button>{" "}
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={this.OnDelete}
                        >
                          🗑
                        </Button>
                      </>
                    ) : (
                      <span className="text-muted">🔒 Read-only</span>
                    )}
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  }

  render() {
    // If mobile prop present, render the two-column table view
    if (this.props.mobile) {
      return this.mobileTwoColumnTable();
    }

    // Desktop: original <tr> (unchanged markup & behavior)
    const it = this.props.item || {};
    const { editMode, editItem, saving } = this.state;
    const canEdit = !!this.props.canEdit;
    const readOnly = !canEdit;

    const displaySellPrice = this.computeSellPrice(it.mrp, it.discount);
    const displaySellPriceWithGst = this.computeSellPriceWithGst(
      displaySellPrice,
      it.gst
    );

    return (
      <>
        <tr>
          <td>{this.props.index + 1}</td>

          {/* PRODUCT NAME */}
          <td>
            {editMode ? (
              <Form.Control
                name="productName"
                value={editItem.productName}
                onChange={this.onChange}
                disabled={readOnly}
              />
            ) : (
              it.productName
            )}
          </td>

          {/* BATCH */}
          <td>
            {editMode ? (
              <Form.Control
                name="batchId"
                value={editItem.batchId}
                onChange={this.onChange}
                disabled={readOnly}
              />
            ) : (
              it.batchId
            )}
          </td>

          {/* QUALITY */}
          <td>
            {editMode ? (
              <Form.Control
                name="qualityId"
                value={editItem.qualityId}
                onChange={this.onChange}
                disabled={readOnly}
              />
            ) : (
              it.qualityId
            )}
          </td>

          {/* HSN */}
          <td>
            {editMode ? (
              <Form.Control
                name="hsncode"
                value={editItem.hsncode}
                onChange={this.onChange}
                disabled={readOnly}
              />
            ) : (
              it.hsncode
            )}
          </td>

          {/* INSTOCK QTY */}
          <td style={{ width: 100 }}>
            {editMode ? (
              <Form.Control
                type="number"
                name="inStockQty"
                value={editItem.inStockQty}
                onChange={this.onNumericChange}
                disabled={readOnly}
              />
            ) : (
              it.inStockQty
            )}
          </td>

          {/* UNIT */}
          <td style={{ width: 120 }}>
            {editMode ? (
              <Form.Select
                name="unit"
                value={editItem.unit}
                required
                align="center"
                className="text-center"
                autoComplete="off"
                onChange={this.onChange}
                disabled={readOnly}
              >
                <option value="piece">piece</option>
                <option value="gram">gram</option>
                <option value="kg">kg</option>
                <option value="ton">ton</option>
                <option value="ml">ml</option>
                <option value="liter">liter</option>
                <option value="meter">meter</option>
              </Form.Select>
            ) : (
              it.unit
            )}
          </td>

          {/* MRP */}
          <td style={{ width: 110 }}>
            {editMode ? (
              <Form.Control
                type="number"
                step="0.01"
                name="mrp"
                value={editItem.mrp}
                onChange={this.onNumericChange}
                disabled={readOnly}
              />
            ) : (
              Number(it.mrp).toFixed(0)
            )}
          </td>

          {/* DISCOUNT */}
          <td style={{ width: 100 }}>
            {editMode ? (
              <Form.Control
                type="number"
                step="0.01"
                name="discount"
                value={editItem.discount}
                onChange={this.onNumericChange}
                disabled={readOnly}
              />
            ) : (
              it.discount
            )}
          </td>

          {/* SELLING PRICE */}
          <td style={{ width: 120 }}>
            {editMode ? (
              <Form.Control
                readOnly
                value={this.computeSellPrice(
                  editItem.mrp,
                  editItem.discount
                ).toFixed(2)}
                disabled={readOnly}
              />
            ) : (
              displaySellPrice.toFixed(0)
            )}
          </td>

          {/* GST */}
          <td style={{ width: 80 }}>
            {editMode ? (
              <Form.Control
                type="number"
                name="gst"
                value={editItem.gst}
                onChange={this.onNumericChange}
                disabled={readOnly}
              />
            ) : (
              it.gst
            )}
          </td>

          {/* SELLING PRICE WITH GST */}
          <td style={{ width: 160 }}>
            {editMode ? (
              <Form.Control
                readOnly
                value={this.computeSellPriceWithGst(
                  this.computeSellPrice(editItem.mrp, editItem.discount),
                  editItem.gst
                ).toFixed(2)}
                disabled={readOnly}
              />
            ) : (
              displaySellPriceWithGst.toFixed(0)
            )}
          </td>

          {/* SUPPLIER */}
          <td style={{ width: 180 }}>
            {editMode ? (
              // If user is supplier and editing their own row, show read-only supplier name + hidden input
              this.props.isSupplierUser &&
              this.props.supplierUserId &&
              String(it.supplierId).toLowerCase() ===
                String(this.props.supplierUserId).toLowerCase() ? (
                <div>
                  <Form.Control
                    readOnly
                    value={
                      this.getSupplierLabelLocal(this.props.supplierUserId) ||
                      this.props.supplierUserId
                    }
                  />
                  <input
                    type="hidden"
                    name="supplierId"
                    value={this.props.supplierUserId}
                  />
                </div>
              ) : (
                // otherwise show normal select (disabled if row is read-only)
                <Form.Select
                  name="supplierId"
                  value={editItem.supplierId ?? ""}
                  onChange={this.onChange}
                  size="sm"
                  disabled={readOnly}
                >
                  <option value="">— None —</option>
                  {(this.props.suppliers || []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name || s.companyName || s.emailId || s.id}
                    </option>
                  ))}
                </Form.Select>
              )
            ) : (
              this.getSupplierLabelLocal(it.supplierId)
            )}
          </td>

          {/* OPTIONS */}
          <td className="text-end" style={{ minWidth: 150 }}>
            {editMode ? (
              <>
                <Button
                  size="sm"
                  variant="success"
                  onClick={this.onSave}
                  disabled={saving || readOnly}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>{" "}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={this.onCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {canEdit ? (
                  <>
                    <Button size="sm" variant="warning" onClick={this.onEdit}>
                      <BsFillPencilFill />
                    </Button>{" "}
                    <Button size="sm" variant="danger" onClick={this.OnDelete}>
                      🗑
                    </Button>
                  </>
                ) : (
                  <span className="text-muted">🔒</span>
                )}
              </>
            )}
          </td>
        </tr>
      </>
    );
  }
}

/* InventoryAddOrEditModal: unchanged from prior (keeps supplier locked for supplier users).
   If you want the modal code included as well, I can paste it — but I left it as-is
   assuming your Add modal already had the read-only supplier logic. */
export class InventoryAddOrEditModal extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    const it = props.item || {};
    this.state = {
      item: {
        id: it.id || undefined,
        storeId: it.storeId || this.context?.storeId,
        productName: it.productName || "",
        remarks: it.remarks || "",
        hsncode: it.hsncode || "",
        batchId: it.batchId || "",
        qualityId: it.qualityId || "",
        mrp: it.mrp ?? "0",
        discount: it.discount ?? "0",
        inStockQty: it.inStockQty ?? "0",
        unit: it.unit || "piece",
        gst: it.gst ?? "0",
        supplierId: it.supplierId || "",
      },
      suppliers: [],
      loadingSuppliers: false,
      errorMessage: "",
      supplierSelectDisabled: false,
    };
  }

  componentDidMount() {
    this.setState({ loadingSuppliers: true });
    this.loadSuppliers()
      .then((list) => {
        const ctx = this.context || {};
        const user = ctx.userData || {};
        const roles = ctx.UserRoles || {};
        const isSupplierUser = user && roles && user.roleId === roles.SUPPLIER;
        const supplierIdRaw = user ? user.storeRoleValue : null;
        if (isSupplierUser && supplierIdRaw) {
          const supplierId = String(supplierIdRaw);
          this.setState((prev) => ({
            item: { ...prev.item, supplierId: supplierId },
            supplierSelectDisabled: true,
            suppliers: list || prev.suppliers,
            loadingSuppliers: false,
          }));
          return;
        }
        this.setState({ suppliers: list || [], loadingSuppliers: false });
      })
      .catch((err) => {
        console.error("loadSuppliers modal error", err);
        this.setState({ loadingSuppliers: false });
      });
  }

  loadSuppliers = () => {
    const storeId = this.context?.storeId;
    if (!storeId) return Promise.resolve([]);
    return fetch(`${process.env.REACT_APP_API}Supplier/GetSuppliers/${storeId}`)
      .then((r) => r.json())
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return list;
      })
      .catch((err) => {
        console.error("Failed to load suppliers", err);
        return [];
      });
  };

  getSupplierLabel = (supplierId) => {
    if (!supplierId) return "";
    const s = (this.state.suppliers || []).find(
      (x) =>
        String(x.id || "").toLowerCase() ===
        String(supplierId || "").toLowerCase()
    );
    if (!s) return supplierId;
    return s.name || s.companyName || s.emailId || s.id;
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({ item: { ...prev.item, [name]: value } }));
  };

  handleSupplierChange = (e) => {
    const val = e.target.value;
    this.setState((prev) => ({ item: { ...prev.item, supplierId: val } }));
  };

  computeSellPrice = () => {
    const it = this.state.item;
    const mrp = parseFloat(it.mrp || 0) || 0;
    const discount = parseFloat(it.discount || 0) || 0;
    const disc = discount > 1 ? discount / 100 : discount;
    const sell = mrp - mrp * disc;
    return Math.round((sell + Number.EPSILON) * 100) / 100;
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const raw = { ...this.state.item };

    if (!raw.productName || !raw.productName.trim()) {
      this.setState({ errorMessage: "Product name is required" });
      return;
    }

    const payload = {
      ...raw,
      storeId: this.context.storeId,
      mrp: raw.mrp === "" ? 0 : parseFloat(raw.mrp),
      discount: raw.discount === "" ? 0 : parseFloat(raw.discount),
      inStockQty: raw.inStockQty === "" ? 0 : parseInt(raw.inStockQty, 10),
      gst: raw.gst === "" ? 0 : parseInt(raw.gst, 10),
      supplierId:
        raw.supplierId && raw.supplierId.trim() !== "" ? raw.supplierId : null,
    };

    this.setState({ errorMessage: "" });

    fetch(
      `${process.env.REACT_APP_API}SupplierInventory/AddOrUpdateInventory`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => {
        if (res.ok) this.props.onHide();
        else {
          res.text().then((t) => alert("Save failed: " + res.status + " " + t));
        }
      })
      .catch((err) => {
        console.error("Save error", err);
        alert("Save failed, see console.");
      });
  };

  render() {
    const it = this.state.item;
    const { suppliers, loadingSuppliers, supplierSelectDisabled } = this.state;

    return (
      <Modal
        {...this.props}
        size="lg"
        centered
        backdrop="static"
        fullscreen="sm-down"
        scrollable
      >
        <Form onSubmit={this.handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {this.props.Type === "Edit" ? "Edit Inventory" : "Add Inventory"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row>
              <Col xs={12}>
                <Form.Group className="mb-2">
                  <Form.Label>Supplier</Form.Label>
                  {loadingSuppliers ? (
                    <div>
                      <Spinner animation="border" size="sm" /> Loading
                      suppliers...
                    </div>
                  ) : supplierSelectDisabled ? (
                    <div>
                      <Form.Control
                        readOnly
                        value={
                          this.getSupplierLabel(it.supplierId) ||
                          it.supplierId ||
                          ""
                        }
                      />
                      <input
                        type="hidden"
                        name="supplierId"
                        value={it.supplierId || ""}
                      />
                      <Form.Text className="text-muted">
                        Supplier is locked to your account.
                      </Form.Text>
                    </div>
                  ) : (
                    <Form.Select
                      name="supplierId"
                      value={it.supplierId || ""}
                      onChange={this.handleSupplierChange}
                      required
                    >
                      <option value="">— None —</option>
                      {suppliers.map((s) => {
                        const label = `${
                          s.name || s.companyName || s.emailId || s.id
                        }${s.companyName ? " (" + s.companyName + ")" : ""}${
                          s.isEnterprise ? " [Enterprise]" : ""
                        }`;
                        return (
                          <option key={s.id} value={s.id}>
                            {label}
                          </option>
                        );
                      })}
                    </Form.Select>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* rest of modal inputs (product name, batch, quality, price, qty, remarks) */}
            <Row>
              <Col xs={12}>
                <Form.Group className="mb-2">
                  <Form.Label>Product Name</Form.Label>
                  <Form.Control
                    name="productName"
                    value={it.productName}
                    onChange={this.handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Batch Id</Form.Label>
                  <Form.Control
                    name="batchId"
                    value={it.batchId}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>

              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Quality Id</Form.Label>
                  <Form.Control
                    name="qualityId"
                    value={it.qualityId}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>

              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>HSN Code</Form.Label>
                  <Form.Control
                    name="hsncode"
                    value={it.hsncode}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>MRP</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="mrp"
                    value={it.mrp}
                    onChange={this.handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Discount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="discount"
                    value={it.discount}
                    onChange={this.handleChange}
                  />
                </Form.Group>
              </Col>

              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Sell.Price</Form.Label>
                  <Form.Control readOnly value={this.computeSellPrice()} />
                </Form.Group>
              </Col>

              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>In Stock Qty</Form.Label>
                  <Form.Control
                    type="number"
                    name="inStockQty"
                    value={it.inStockQty}
                    onChange={this.handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Unit</Form.Label>
                  <Form.Select
                    name="unit"
                    value={it.unit}
                    required
                    align="center"
                    className="text-center"
                    autoComplete="off"
                    onChange={this.handleChange}
                  >
                    <option value="piece">piece</option>
                    <option value="gram">gram</option>
                    <option value="kg">kg</option>
                    <option value="ton">ton</option>
                    <option value="ml">ml</option>
                    <option value="liter">liter</option>
                    <option value="meter">meter</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>GST (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="gst"
                    value={it.gst}
                    onChange={this.handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-2">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="remarks"
                value={it.remarks}
                onChange={this.handleChange}
              />
            </Form.Group>

            {this.state.errorMessage && (
              <div className="text-danger mt-2">{this.state.errorMessage}</div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button type="submit" variant="primary">
              Save
            </Button>
            <Button variant="secondary" onClick={this.props.onHide}>
              Close
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }
}
